/**
 * Subscription slice only: legacy plan names, discount shapes, and trial-plan defaults.
 * Full signup-flow parsing lives in `signup-flow-store.schema.ts`.
 */
import {
  LEGACY_KOTLIN_MEAL_PLAN_TO_PRICING_PLAN,
  TRIAL_PLAN_DEFAULT_DISCOUNT,
  TRIAL_WEEK_PLAN_DEFAULT_DISCOUNT,
} from "../constants/subscription-normalisation.constants";
import {
  pricingPlanEnum,
  type TDiscountCodeType,
  type TPricingPlan,
} from "../types/enums";

const VALID_PRICING_PLANS = new Set<string>(pricingPlanEnum.options);

/**
 * Normalises a plan type that may be a legacy Kotlin MealPlans enum name
 * to the current PricingPlan string (e.g. "PlanTrial14x14" → "TRIAL").
 */
export function normalisePlanType(
  value: string | undefined | null,
  fallback: TPricingPlan,
): TPricingPlan {
  if (!value) {
    return fallback;
  }
  if (VALID_PRICING_PLANS.has(value)) {
    return value as TPricingPlan;
  }
  return LEGACY_KOTLIN_MEAL_PLAN_TO_PRICING_PLAN[value] ?? fallback;
}

export function getDiscountCode(discount: unknown): string | undefined {
  if (typeof discount === "string") {
    const legacyCode = discount.trim();
    return legacyCode || undefined;
  }

  if (discount && typeof discount === "object" && !Array.isArray(discount)) {
    const code = (discount as Record<string, unknown>)["discountCode"];
    if (typeof code === "string") {
      const normalisedCode = code.trim();
      return normalisedCode || undefined;
    }
  }

  return undefined;
}

type DiscountShape = {
  discountCode: string;
  type?: TDiscountCodeType;
  value?: string;
};

function resolveFallbackMetadata(
  fallback: DiscountShape | undefined,
  discountCode: string,
): { type: TDiscountCodeType | undefined; value: string | undefined } {
  if (fallback?.discountCode !== discountCode) {
    return { type: undefined, value: undefined };
  }
  return { type: fallback.type, value: fallback.value };
}

export function getDefaultDiscountForTrialPlan(
  planType: string | undefined,
): DiscountShape | undefined {
  if (!planType) {
    return undefined;
  }
  if (planType === "TRIAL") {
    return { ...TRIAL_PLAN_DEFAULT_DISCOUNT };
  }
  if (planType === "TRIAL_WEEK") {
    return { ...TRIAL_WEEK_PLAN_DEFAULT_DISCOUNT };
  }
  return undefined;
}

/**
 * Normalises persisted discount data across legacy and current store shapes.
 */
export function normaliseDiscount(
  discount: unknown,
  fallback?: DiscountShape,
): DiscountShape | undefined {
  const discountCode = getDiscountCode(discount);

  if (!discountCode) {
    return fallback ? { ...fallback } : undefined;
  }

  const { type: fallbackType, value: fallbackValue } = resolveFallbackMetadata(
    fallback,
    discountCode,
  );

  if (discount && typeof discount === "object" && !Array.isArray(discount)) {
    const discountObj = discount as Record<string, unknown>;
    return {
      discountCode,
      type: (discountObj["type"] as TDiscountCodeType | undefined) ?? fallbackType,
      value:
        typeof discountObj["value"] === "string"
          ? discountObj["value"]
          : fallbackValue,
    };
  }

  return { discountCode, type: fallbackType, value: fallbackValue };
}
